/**
 * El estado de la conexión con Snov, compartido por las pantallas de Emails (lo arma `EmailsLayout`):
 * - si está conectada, cuándo se actualizó y si se está actualizando ahora;
 * - `version`, que sube cada vez que hay datos nuevos (terminó «Actualizar ahora» o la programada: se nota porque
 *   cambia `actualizadoEn`). Las páginas la usan en su clave y se vuelven a pedir solas;
 * - `actualizar()` («Actualizar ahora», del equipo): pide la sincronización (202).
 *
 * Cómo se mira: cada 3 s mientras se actualiza y cada minuto si no (la programada corre sola en el servidor), con
 * `usePolling`: no pide con la pestaña oculta, pide al volver y, con 3 errores seguidos, para y lo dice. Cada lectura
 * lleva un número: una respuesta vieja que llega tarde (pedida antes del 202) no pisa a la nueva.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { emailsApi } from "./api";
import type { EstadoEmails } from "./types";
import { leerError, leerRespuesta, textoError } from "../wa-oficial/logic/respuesta";
import { usePolling } from "../wa-oficial/hooks/usePolling";

export interface EstadoEmailsCtx {
  estado: EstadoEmails | null;
  error: string;
  version: number;
  actualizando: boolean;
  /** 3 errores seguidos: se dejó de mirar el estado (hasta `recargar`) */
  detenido: boolean;
  /** Devuelve el texto del problema, o "" si la actualización arrancó. */
  actualizar: () => Promise<string>;
  recargar: () => void;
}

const Ctx = createContext<EstadoEmailsCtx | null>(null);

export const CADA_MS_AVANCE = 3000;
export const CADA_MS_ESTADO = 60_000;

export function EstadoEmailsProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoEmails | null>(null);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const visto = useRef<{ enCurso: boolean; actualizadoEn: string | null } | null>(null);
  const seq = useRef(0);
  const vivo = useRef(true);

  const aplicar = useCallback((e: EstadoEmails) => {
    const antes = visto.current;
    // Datos nuevos: terminó una actualización (la nuestra) o se movió la fecha (también la programada).
    if (antes && ((antes.enCurso && !e.enCurso) || (!!e.actualizadoEn && e.actualizadoEn !== antes.actualizadoEn))) setVersion((v) => v + 1);
    visto.current = { enCurso: e.enCurso, actualizadoEn: e.actualizadoEn };
    setEstado(e);
  }, []);

  /** Lee /estado. Lanza si falla (así `usePolling` cuenta el error); una respuesta vieja se descarta sin error. */
  const leer = useCallback(async (): Promise<void> => {
    const mio = ++seq.current;
    let l;
    try {
      l = leerRespuesta<EstadoEmails>(await emailsApi.estado());
    } catch (e) {
      if (vivo.current && mio === seq.current) setError(textoError(leerError(e)));
      throw e;
    }
    if (!vivo.current || mio !== seq.current) return;
    if (!l.ok || !l.data) {
      setError(l.ok ? "El servidor no devolvió el estado." : textoError(l));
      throw new Error("estado");
    }
    setError("");
    aplicar(l.data);
  }, [aplicar]);

  useEffect(() => {
    vivo.current = true;
    leer().catch(() => undefined);
    return () => {
      vivo.current = false;
    };
  }, [leer]);

  const enCurso = !!estado?.enCurso;
  const polling = usePolling(leer, enCurso ? CADA_MS_AVANCE : CADA_MS_ESTADO, []);

  const actualizar = useCallback(async (): Promise<string> => {
    try {
      const l = leerRespuesta<{ enCurso: boolean }>(await emailsApi.sincronizar());
      if (!l.ok) return textoError(l);
      // Lo que se pidió antes del 202 ya no vale: diría «no se está actualizando» y cortaría el seguimiento.
      seq.current++;
      visto.current = { enCurso: true, actualizadoEn: visto.current?.actualizadoEn ?? null };
      setEstado((e) => (e ? { ...e, enCurso: true } : e));
      return "";
    } catch (e) {
      const x = leerError(e);
      // Ya había una corriendo: no es un error para quien apretó, se muestra el avance de esa.
      if (x.code === "SINCRONIZANDO") {
        leer().catch(() => undefined);
        return "";
      }
      return textoError(x);
    }
  }, [leer]);

  const recargar = useCallback(() => {
    if (polling.detenido) polling.reintentar();
    else leer().catch(() => undefined);
  }, [polling, leer]);

  return (
    <Ctx.Provider value={{ estado, error, version, actualizando: enCurso, detenido: polling.detenido, actualizar, recargar }}>
      {children}
    </Ctx.Provider>
  );
}

/** Fuera del proveedor (un test que monta una página sola) devuelve un estado vacío que no rompe nada. */
export function useEstadoEmails(): EstadoEmailsCtx {
  return (
    useContext(Ctx) || {
      estado: null,
      error: "",
      version: 0,
      actualizando: false,
      detenido: false,
      actualizar: async () => "",
      recargar: () => undefined,
    }
  );
}
